import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Inbox, Clock, Pencil, Trash2, Loader2 } from 'lucide-react';
import { formatUsd, deleteTimeEntry } from '@/lib/timeEntriesData';
import { useAuth } from '@/context/AuthContext';
import EditTimeEntryDialog from '@/components/clock/EditTimeEntryDialog';
import { toast } from 'sonner';

export const TodayLog = ({ entries = [], jobs = [], onChange }) => {
  const { t } = useTranslation();
  const { isOwner } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalHours = entries.reduce((s, e) => s + (Number(e?.hours) || 0), 0);
  const totalCost = entries.reduce((s, e) => s + (Number(e?.cost) || 0), 0);

  const handleEdit = (entry) => {
    setActiveEntry(entry);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteTimeEntry(confirmDelete.id);
      toast.success(t('crewClock.today.entry_deleted'));
      setConfirmDelete(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.message || t('crewClock.today.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[hsl(var(--primary))]" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('crewClock.today_log')}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {t('crewClock.entries.count', {
              count: entries.length,
              hours: totalHours.toFixed(2),
              cost: formatUsd(totalCost),
            })}
          </div>
        </div>
        {entries.length === 0 ? (
          <div className="mt-4 flex flex-col items-center text-center py-6">
            <div className="size-9 rounded-md border border-border bg-[hsl(var(--muted))] flex items-center justify-center text-muted-foreground">
              <Inbox size={16} strokeWidth={1.75} />
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              {t('crewClock.no_entries_today')}
            </div>
          </div>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="today-log-list">
            {entries.map((e) => (
              <li
                key={e.id}
                className="flex items-center gap-3 rounded-md border border-border bg-[hsl(var(--background))]/40 px-3 py-2.5"
                data-testid={`today-log-${e.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground truncate">
                      {e.name}
                    </span>
                    <Badge
                      variant="outline"
                      className="uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--muted))] text-muted-foreground border-border"
                    >
                      {e.job}
                    </Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {e.date} · {t('crewClock.entries.closed')}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-foreground">
                    {Number(e.hours || 0).toFixed(2)}h
                  </div>
                  <div className="text-xs text-[hsl(var(--success))]">
                    {formatUsd(e.cost)}
                  </div>
                </div>
                {isOwner ? (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(e)}
                      className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] h-7 w-7"
                      aria-label={t('crewClock.today.edit_aria')}
                      data-testid={`today-log-edit-${e.id}`}
                    >
                      <Pencil size={13} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setConfirmDelete(e)}
                      className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 h-7 w-7"
                      aria-label={t('crewClock.today.delete_aria')}
                      data-testid={`today-log-delete-${e.id}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {isOwner ? (
        <EditTimeEntryDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode="edit"
          entry={activeEntry}
          jobs={jobs}
          onSaved={() => onChange?.()}
        />
      ) : null}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('crewClock.today.delete_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('crewClock.today.delete_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-[hsl(var(--muted))] text-foreground hover:bg-[hsl(var(--muted))]/80"
            >
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
              data-testid="today-log-delete-confirm-action"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {t('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TodayLog;
